import React, { useEffect, useRef, useState } from "react";
import "../styles/LoopVideo.css";

const prefersReducedMotion = () =>
	typeof window !== "undefined" &&
	window.matchMedia &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Silent looping video that:
 * - only downloads/plays while on screen (IntersectionObserver), saving bandwidth on mobile
 * - never autoplays for visitors who ask for reduced motion
 * - always has a visible pause/play control (WCAG 2.2.2 — moving content longer than 5s)
 */
export default function LoopVideo({ src, poster, label, className = "", eager = false }) {
	const ref = useRef(null);
	const [playing, setPlaying] = useState(false);
	const userPaused = useRef(prefersReducedMotion());

	useEffect(() => {
		const video = ref.current;
		if (!video) return;
		// React does not reliably render the `muted` attribute; iOS needs it set to autoplay.
		video.muted = true;
		video.defaultMuted = true;

		const tryPlay = () => {
			if (userPaused.current) return;
			const p = video.play();
			if (p && p.catch) p.catch(() => setPlaying(false));
		};

		if (!("IntersectionObserver" in window)) {
			tryPlay();
			return;
		}
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) tryPlay();
				else video.pause();
			},
			{ threshold: 0.25 }
		);
		io.observe(video);
		return () => io.disconnect();
	}, []);

	const toggle = () => {
		const video = ref.current;
		if (!video) return;
		if (video.paused) {
			userPaused.current = false;
			video.play().catch(() => {});
		} else {
			userPaused.current = true;
			video.pause();
		}
	};

	return (
		<div className={`loop-video ${className}`}>
			<video
				ref={ref}
				src={src}
				poster={poster}
				muted
				loop
				playsInline
				preload={eager ? "auto" : "none"}
				aria-label={label}
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
			/>
			<button
				type="button"
				className="loop-video__toggle"
				onClick={toggle}
				aria-label={playing ? `Pause video: ${label}` : `Play video: ${label}`}
			>
				{playing ? (
					<svg viewBox="0 0 16 16" aria-hidden="true">
						<rect x="3.5" y="2.5" width="3" height="11" />
						<rect x="9.5" y="2.5" width="3" height="11" />
					</svg>
				) : (
					<svg viewBox="0 0 16 16" aria-hidden="true">
						<path d="M4 2.5v11l9-5.5z" />
					</svg>
				)}
			</button>
		</div>
	);
}
